import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useStore } from "@Store/";
import { setSmoothScroll } from "@Store/";
import {
  setHideHeader,
  setOnBannerPosition,
} from "@Store/";
import { setScrollDirection } from "@Store/";
import { isDesktop } from "react-device-detect";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const SmoothScroll = ({ callbacks = {} }) => {
  const [store, dispatch] = useStore();

  const {
    smoothScroll,
    scrollDirection,
    hideHeader,
    showHeaderBackground,
  } = store;

  const smoothScrollRef = useRef();
  const scrollDirectionRef = useRef();
  const showHeaderRef = useRef();
  const showHeaderBackgroundRef = useRef();
  const scrollInstanceRef = useRef(null);
  const refreshFrameRef = useRef(null);

  const showHeaderThreshold = 0.09;
  const showHeaderBackgroundThreshold = 0.02;

  useEffect(() => {
    smoothScrollRef.current = smoothScroll;
  }, [smoothScroll]);

  useEffect(() => {
    scrollDirectionRef.current = scrollDirection;
  }, [scrollDirection]);

  useEffect(() => {
    showHeaderRef.current = hideHeader;
  }, [hideHeader]);

  useEffect(() => {
    showHeaderBackgroundRef.current = showHeaderBackground;
  }, [showHeaderBackground]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    setHideHeader(dispatch, false);

    ScrollTrigger.clearScrollMemory("manual");

    window.scrollTo(0, 0);

    const smoother = ScrollSmoother.create({
      smooth: 1.3,
      smoothTouch: false,
      effects: false,
      normalizeScroll: false,
      ignoreMobileResize: true,

      onUpdate: (self) => {
        const progress = self?.progress ?? 0;
        const direction = self?.scrollTrigger?.direction;

        if (
          progress > showHeaderThreshold &&
          !showHeaderRef.current
        ) {
          setHideHeader(dispatch, true);
        } else if (
          progress <= showHeaderThreshold &&
          showHeaderRef.current
        ) {
          setHideHeader(dispatch, false);
        }

        if (
          progress > showHeaderBackgroundThreshold &&
          !showHeaderBackgroundRef.current
        ) {
          setOnBannerPosition(dispatch, true);
        } else if (
          progress <= showHeaderBackgroundThreshold &&
          showHeaderBackgroundRef.current
        ) {
          setOnBannerPosition(dispatch, false);
        }

        if (scrollDirectionRef.current !== direction) {
          setScrollDirection(dispatch, direction);
        }
      },
    });

    scrollInstanceRef.current = smoother;

    smoother.scrollTo(0, false);

    setSmoothScroll(dispatch, smoother);

    refreshFrameRef.current = requestAnimationFrame(() => {
      refreshFrameRef.current = requestAnimationFrame(() => {
        if (!scrollInstanceRef.current) return;

        scrollInstanceRef.current.scrollTo(0, false);
        scrollInstanceRef.current.refresh();

        ScrollTrigger.update();
        ScrollTrigger.refresh(true);
      });
    });

    const handleStartLoading = () => {
      if (window.pathName === window.location.pathname) return;

      if (isDesktop && scrollInstanceRef.current) {
        scrollInstanceRef.current.scrollTo(0, false);
      } else {
        window.scrollTo(0, 0);
      }

      ScrollTrigger.clearScrollMemory("manual");

      window.pathName = window.location.pathname;
    };

    window.addEventListener(
      "startLoading",
      handleStartLoading,
    );

    return () => {
      window.removeEventListener(
        "startLoading",
        handleStartLoading,
      );

      if (refreshFrameRef.current) {
        cancelAnimationFrame(refreshFrameRef.current);
      }

      if (scrollInstanceRef.current) {
        scrollInstanceRef.current.kill();
        scrollInstanceRef.current = null;
      }

      setSmoothScroll(dispatch, null);

      ScrollTrigger.clearMatchMedia();
    };
  }, [callbacks.pathname, dispatch]);

  useEffect(() => {
    if (!scrollInstanceRef.current) return;

    scrollInstanceRef.current.refresh();
    ScrollTrigger.refresh();
  }, [store.resized]);

  return null;
};

export default SmoothScroll;